import React, { useCallback } from 'react';
import { Navigate, Outlet } from 'react-router';
import { LoadingSpinner } from '@/components/loading/Loading';
import { useAuth } from '@/libs/device-auth/hooks/useAuth';
import { useSelection } from '@/libs/onboarding/hooks/useSelection';
import { AppScreen } from '@/types/common';
import { storageSvc } from '@/services/storageServiceClient';
import { STORAGE_KEYS } from '@/utils/constants';
import { useDeviceEvents } from '@/hooks/useDeviceEvents';
import { useAppVersionSync } from '@/hooks/useAppVersionSync';
import { DeviceEvent, DeviceEventActions } from '@oolio-group/domain';

/**
 * DeviceConfigLayout wraps routes that require complete organization/store/device selection
 * It renders the nested routes via Outlet when all selections are complete
 */
export const DeviceConfigLayout: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();

  const {
    organization,
    store,
    device,
    isLoading: selectionsLoading,
    clearSelections,
  } = useSelection(storageSvc);

  const handleLogout = useCallback(
    async (event: DeviceEvent) => {
      if (event.deviceId !== device?.id) {
        // If the event is not for the current device, ignore it
        return;
      }
      try {
        await storageSvc.remove(STORAGE_KEYS.FINGERPRINT_UUID);
        await clearSelections();
        await logout();
      } catch (error) {
        console.error('ORB Device Event: Error during remote logout:', error);
      }
    },
    [clearSelections, device?.id, logout],
  );

  useDeviceEvents([
    {
      action: DeviceEventActions.LOGOUT_ACTION,
      handler: handleLogout,
    },
  ]);

  useAppVersionSync(device?.id, device?.name);

  const isLoading = authLoading || selectionsLoading;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // If not authenticated, redirect to landing
  if (!isAuthenticated) {
    return <Navigate to={AppScreen.LANDING} replace />;
  }

  // If full selection is required but one of the selections is missing
  if (!organization || !store || !device) {
    return <Navigate to={AppScreen.GET_STARTED} replace />;
  }

  // If all checks pass, render the nested routes via Outlet
  return <Outlet />;
};
